import {
  FINAL_CELL,
  LADDERS_LEVELS,
  LADDERS_LIVE_STYLES,
  LADDERS_PLAYER_COUNTS,
  LADDERS_STYLES,
  type LaddersLevel,
  type LaddersMove,
  type LaddersPlayerCount,
  type LaddersStyle,
  type LaddersView,
} from "@bing/engine-ladders";
import { isolate } from "@bing/shared";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Avatar } from "../components/Avatar";
import { GameIcon } from "../components/GameIcon";
import { DIE_THROW_MS, Die3D } from "../game/backgammon-dice";
import { prefersReducedMotion } from "../game/card-motion";
import { LaddersBoard } from "../game/ladders/LaddersBoard";
import { LaddersScene, type DieDraw, type LaddersSceneFit } from "../game/ladders/LaddersScene";
import { LADDERS_PIECE_COLORS } from "../game/ladders/looks";
import { TurnTimerSlot, type TableTurnTimer } from "../game/online-lifecycle";
import { botOrdinal } from "../game/opponent-number";
import { presetAvatarFor } from "../game/seat-avatar";
import {
  resolveSeatAvatar,
  resolveSeatName,
  type SeatIdentity,
  type SeatPlayer,
} from "../game/seat-identity";
import { faDigits } from "../i18n/digits";
import { fa } from "../i18n/fa";
import { playSfx } from "../sound/sfx";

/* ------------------------------------------------------------------ *
 * مار پله — the table (card-86; round 2, batch W2).
 *
 * W1's `LaddersScene` lays the screen out (the look, the die's band above
 * the board, the measured board slot) and W1's `LaddersBoard` draws the
 * board and moves the pieces; this file only wires the game to them: the
 * die (`renderDie`), the seat strip, the hint and the turn timer (the
 * scene's `footer`), and the exit (an overlay, `children`).
 *
 * A TURN IS TWO TAPS (the operator, round 2: «they tap to roll the die,
 * then tap their piece to move it»): the DIE is the roll control — the
 * footer has no roll button — and YOUR PIECE is the move control. The
 * server throws (from the state's seeded RNG) and is authoritative for
 * both (`legal`), so the table never guesses a rule: an overshoot, a 6, a
 * hit — the frame says what happened and whose turn it is now.
 *
 * ALMOST NO LOAD ON THE PHONE. Nothing here loops: the die is the shared
 * finite throw (WAAPI, ~1 s, then nothing), the board's motion is W1's
 * finite run, the rings on the die and the piece are STATIC, and the one
 * timer this file arms is a one-shot per watched throw (the die's landing,
 * for the hint and the piece's highlight). The motion's END is the board's
 * own `onMotionEnd` — a callback, not a timer. An idle table runs nothing.
 * Every effect depends on numbers and booleans only — never on the
 * frame's objects, which are new on every update.
 * ------------------------------------------------------------------ */

/** Base <button> reset (Tailwind preflight is off → reset explicitly). */
const BTN = "appearance-none m-0 cursor-pointer";

/**
 * The strip's faces show only on a column at least this wide (card-86 review, 2026-11-03): at
 * 240 / 277 px (the zoomed phones) a face left a name ONE letter wide — there the colour dot, the
 * name and the cell are the strip, and the hint line and the lit strip say whose turn it is.
 */
export const LADDERS_ROOMY_MIN_W = 300;

/** «۲ نفره» / «۴ نفره» — the sizes the platform offers, worded as the lobby's two public rooms are.
 *  ONE list for the solo start screen and the private host's «تنظیمات میز». */
export const LADDERS_PLAYER_CHOICES: readonly (readonly [LaddersPlayerCount, string])[] =
  LADDERS_PLAYER_COUNTS.map((n) => [n, fa.ladders.playersOption(faDigits(n))] as const);

/** ROUND 2 — «آسان» / «متوسط» / «سخت», easiest first: ONE list for the solo start screen and the
 *  private host's «تنظیمات میز» (a public table is always متوسط and offers no choice). */
export const LADDERS_LEVEL_CHOICES: readonly (readonly [LaddersLevel, string])[] =
  LADDERS_LEVELS.map((level) => [level, fa.difficulty[level]] as const);

/**
 * ROUND 2 — the looks THIS BUILD can draw, in the engine's order (the pictures, then «اورجینال»):
 * ONE list for both pickers. Filtered by `LADDERS_LIVE_STYLES`, so a look that is not live is never
 * offered — bg2 joined this list by itself the day it went live.
 */
export const LADDERS_STYLE_CHOICES: readonly (readonly [LaddersStyle, string])[] =
  LADDERS_STYLES.filter((style) => LADDERS_LIVE_STYLES.includes(style)).map(
    (style) => [style, fa.ladders.styleNames[style]] as const,
  );

/**
 * What THIS SCREEN has seen of the two counters — and whether the latest change is one to SHOW.
 *
 * `animate` is true only when the frame moved each counter by 0 or 1 (the frame of a throw or a move
 * this screen watched happen). The FIRST frame after a mount (a resume, a reconnect that remounted
 * the table) and any JUMP (a reconnect that missed turns, a rematch's fresh game) paint the end
 * state — nothing replays a turn nobody witnessed. `throwing` latches with the die's key (the roll
 * counter): the die throws only for a watched throw. `landed` turns true once that throw has
 * finished ({@link DIE_THROW_MS}), so neither the hint nor your piece's highlight tells the throw's
 * outcome while the die is still in the air.
 */
interface CounterTrack {
  readonly rollCount: number;
  readonly moveCount: number;
  readonly animate: boolean;
  readonly throwing: boolean;
  readonly landed: boolean;
}

/**
 * WHICH OF THE TWO EVENTS IS THE LATEST — the view carries both (`lastRoll`, `lastMove`), never
 * their order. Read off the phase: a pending die (the move phase) means the throw came last; in the
 * roll phase an OVERSHOOT throw came last (it has no move — any later move needs a later throw,
 * which would have replaced it), and otherwise the move of that throw did. Pure.
 */
export function laddersLatest(
  view: Pick<LaddersView, "phase" | "lastRoll" | "lastMove">,
): "roll" | "move" | null {
  if (view.lastRoll === null) return view.lastMove === null ? null : "move";
  if (view.phase === "move" || view.lastRoll.overshoot || view.lastMove === null) return "roll";
  return "move";
}

/**
 * THE ONE-LINE HINT under the board — the latest NEWS once the board has SHOWN it, else what to
 * do. A hit (who hit whom, named) and your own ladder ↑ / snake ↓ wait for the piece to STOP
 * (`moved`: the board's `onMotionEnd` for this move), so the line never tells a climb, a slide or a
 * hit that is still to come; your 6 rolls again (after its move, or at once on a 6 that could not
 * move) and «باید دقیقاً N بیاری» wait for the die to land. Without news: «روی مهره‌ات بزن» once
 * your die has landed, «روی تاس بزن» on your throw, else whose turn it is. Pure, so every branch is
 * pinned by `ladders-table.test.tsx`.
 */
export function laddersHint({
  view,
  canRoll,
  canMove,
  landed,
  moved,
  nameOf,
}: {
  view: LaddersView;
  /** `legal` holds the throw — the server's word. */
  canRoll: boolean;
  /** `legal` holds the move — the server's word. */
  canMove: boolean;
  /** The last throw's die has landed (a still die — the first frame — counts as landed). */
  landed: boolean;
  /** The board has finished (or skipped) the last move's motion — its `onMotionEnd` for it. */
  moved: boolean;
  nameOf: (seat: number) => string;
}): string {
  if (view.status !== "playing") return "";
  const latest = laddersLatest(view);
  const move = view.lastMove;
  if (latest === "move" && moved && move !== null) {
    const hit = move.captured.map((c) => c.seat);
    if (hit.length > 0) {
      if (hit.includes(view.you)) return fa.ladders.captureOnYou(nameOf(move.seat));
      // Two pieces never share a board cell, so ONE is hit in play; a crafted frame names them all.
      const names =
        hit.length === 1
          ? nameOf(hit[0] as number)
          : hit.map((seat) => isolate(nameOf(seat))).join(" و ");
      return move.seat === view.you
        ? fa.ladders.captureByYou(names)
        : fa.ladders.captureOther(nameOf(move.seat), names);
    }
    if (move.seat === view.you) {
      if (move.jump?.kind === "ladder") return fa.ladders.hintLadder;
      if (move.jump?.kind === "snake") return fa.ladders.hintSnake;
      if (move.again) return fa.ladders.hintSixAgain;
    }
  }
  const roll = view.lastRoll;
  if (latest === "roll" && landed && roll !== null && roll.seat === view.you && roll.overshoot) {
    // THE OPERATOR'S RULE says so first: a 6 that cannot move still rolls again.
    return roll.again
      ? fa.ladders.hintSixAgain
      : fa.ladders.hintExact(faDigits(FINAL_CELL - roll.from));
  }
  if (canMove && landed) return fa.ladders.hintMove;
  if (canRoll) return fa.ladders.hintRoll;
  return fa.ladders.turnOf(nameOf(view.turn));
}

/** The seat strip's figure for a cell: «شروع» off the board, else the cell's number. */
export function laddersCellFigure(cell: number): string {
  return cell > 0 ? faDigits(cell) : fa.ladders.atStart;
}

/**
 * What a seat is called: a real person's own name, else «شما» / «حریف N». The bot number is
 * ABSOLUTE (its rank among the bot seats — `botOrdinal`), so the same bot is the same «حریف N» on
 * every player's screen. Shared with the end card, so the two always name a seat the same.
 */
export function laddersSeatName(
  seat: number,
  view: Pick<LaddersView, "you" | "playerCount">,
  self: SeatIdentity | null | undefined,
  players: readonly SeatPlayer[] | undefined,
): string {
  const isBot = (s: number): boolean =>
    s !== view.you && !(players ?? []).some((p) => p.seat === s && p.isBot !== true);
  return resolveSeatName(seat, view.you, self, players, {
    you: fa.ladders.you,
    fallback: (s) => `${fa.ladders.opponent} ${faDigits(botOrdinal(s, view.playerCount, isBot))}`,
  });
}

/** Seat-indexed piece colour — the board's own list, so a strip and its pawn always agree. */
export function laddersSeatColor(seat: number): string {
  return LADDERS_PIECE_COLORS[seat] ?? LADDERS_PIECE_COLORS[0] ?? "#e8b923";
}

export function LaddersTable({
  view,
  legal,
  onMove,
  onLeave,
  self,
  players,
  timer,
}: {
  view: LaddersView;
  legal: readonly LaddersMove[];
  onMove: (move: LaddersMove) => void;
  onLeave: () => void;
  /** Your own profile — your strip's name and face. */
  self?: SeatIdentity | null;
  /** The online roster (a bot, or a human the server resolved). Omitted offline → «حریف N». */
  players?: readonly SeatPlayer[];
  /** The live online turn countdown. Omitted offline. */
  timer?: TableTurnTimer | null;
}): ReactNode {
  /*
   * THE COUNTER TRACK — derived from the two counters during render (React's «adjusting state when
   * a prop changes»), so the frame that brings a new throw is ALSO the frame that mounts its die
   * with `throwing` on: the die latches `throwing` at mount, and an effect-set flag would arrive
   * one render too late (the die would mount still and never throw).
   */
  const [track, setTrack] = useState<CounterTrack>(() => ({
    rollCount: view.rollCount,
    moveCount: view.moveCount,
    animate: false,
    throwing: false,
    landed: true,
  }));
  /* The last move whose MOTION the board has finished or skipped (its `onMotionEnd`). A key that is
   * not the current move's — the previous move, ended early by this one — tells nothing. */
  const [motionEnded, setMotionEnded] = useState<number | null>(null);
  if (track.rollCount !== view.rollCount || track.moveCount !== view.moveCount) {
    const dr = view.rollCount - track.rollCount;
    const dm = view.moveCount - track.moveCount;
    const watched = dr >= 0 && dm >= 0 && dr <= 1 && dm <= 1;
    const thrown = dr !== 0;
    // The die flies only for a watched throw — and not under reduced motion, where it lands at once
    // (`Die3D` places it at rest), so nothing waits for a throw nobody sees.
    const flies = thrown && watched && !prefersReducedMotion();
    setTrack({
      rollCount: view.rollCount,
      moveCount: view.moveCount,
      animate: watched,
      throwing: thrown ? watched : track.throwing,
      landed: thrown ? !flies : track.landed,
    });
    // A counter that went DOWN is a fresh game (a rematch at the same table): the old game's
    // motion end must never stand for the new game's move of the same number.
    if (dr < 0 || dm < 0) setMotionEnded(null);
  }
  // The die's landing — ONE one-shot timer per watched throw, keyed on the counter alone.
  useEffect(() => {
    if (track.landed) return undefined;
    const at = track.rollCount;
    const id = window.setTimeout(
      () => setTrack((t) => (t.rollCount === at && !t.landed ? { ...t, landed: true } : t)),
      DIE_THROW_MS,
    );
    return () => window.clearTimeout(id);
  }, [track.rollCount, track.landed]);
  // The throw's one voice — منچ's dice clip, from the START of the throw (its impact is 0.70 s in,
  // with the die's contact). Never on a mount or a jump: a resume replays no sound.
  useEffect(() => {
    if (track.throwing) playSfx("diceRoll");
  }, [track.rollCount, track.throwing]);
  const onMotionEnd = useCallback((key: number) => setMotionEnded(key), []);

  const you = view.you;
  const seatCount = view.playerCount;
  const playing = view.status === "playing";
  // THE SERVER'S WORD (`legal`) — the view's `canRoll` / `canMove` say the same in every frame.
  const canRoll = playing && legal.some((m) => m.kind === "roll");
  const canMove = playing && legal.some((m) => m.kind === "move");
  // Your piece lights up — and takes a tap — once YOUR die has landed: the highlight would otherwise
  // tell that the throw can move (no overshoot) while the die is still in the air. …AND once the last
  // move's motion has ENDED (the board's `onMotionEnd` for this `moveCount` — a first paint, a skip
  // and a cut-short motion all report it): the hit circle sits on the piece's END cell, so a fast
  // throw after a 6 would otherwise put it where the still-walking piece is not.
  const movable = canMove && track.landed && motionEnded === view.moveCount;
  const nameOf = (seat: number): string => laddersSeatName(seat, view, self, players);
  const hint = laddersHint({
    view,
    canRoll,
    canMove,
    landed: track.landed,
    moved: motionEnded === view.moveCount,
    nameOf,
  });
  const roller = view.roller ?? view.turn;

  const strip = (seat: number, faces: boolean): ReactNode => {
    const colour = laddersSeatColor(seat);
    const face = resolveSeatAvatar(seat, you, self, players, presetAvatarFor);
    const active = playing && view.turn === seat;
    const cell = view.positions[seat] ?? 0;
    return (
      // ONE LINE, `1.75em` of its own text: a fixed height for a given text zoom (nothing in it
      // wraps), which grows WITH the zoom instead of clipping the line.
      <div
        key={seat}
        data-testid={`ladders-strip-${seat}`}
        data-active={active ? "true" : undefined}
        className="flex items-center gap-1 rounded-lg px-1.5 text-caption"
        style={{
          height: "1.75em",
          background: active ? "rgba(255,255,255,0.08)" : "transparent",
          boxShadow: active ? `inset 0 0 0 1px ${colour}` : undefined,
          minWidth: 0,
        }}
      >
        <span
          aria-hidden
          data-ladders-dot={seat}
          style={{ width: 10, height: 10, borderRadius: "50%", background: colour, flex: "none" }}
        />
        {faces ? (
          <Avatar
            avatarId={face.avatarId}
            avatarPhotoId={face.avatarPhotoId}
            size={18}
            official={face.official}
            ringClass={active ? "border-secondary" : "border-outline-variant"}
          />
        ) : null}
        <span dir="auto" className="min-w-0 flex-1 truncate text-on-surface">
          {nameOf(seat)}
        </span>
        <span
          data-testid={`ladders-cell-${seat}`}
          aria-label={cell > 0 ? fa.ladders.cellLabel(faDigits(cell)) : fa.ladders.atStart}
          className="flex-none font-bold text-secondary"
        >
          {laddersCellFigure(cell)}
        </span>
      </div>
    );
  };

  /*
   * THE DIE IS THE ROLL CONTROL (round 2) — W1's band above the board hands this its slot (≥ 44 px).
   * ALWAYS MOUNTED AND ALWAYS SHOWN: it is the thing to tap, before the first throw too (dimmed only
   * then, and only when the throw is not yours — there is no throw on it yet). It re-mounts per
   * throw (`key`) and throws only for a throw this screen watched happen. Live the moment the server
   * says so — a tap during the last move's motion simply throws. ⚠️ NO RING, NO OUTLINE, NO BORDER
   * around it, in ANY state (the operator, 2026-11-04: «the die's area has a yellow outline around
   * it. I don't want that shown at all»): your turn is told by the hint, and the die is never dimmed
   * then. No tap highlight either (Android's WebView would flash a box on it). A raw <button>: the
   * throw answers with the dice clip, so it plays no UI tap of its own.
   */
  // `draw` (W1's scene): how big to draw the die in its place and how far to raise the drawing — on
  // the smallest screens a little smaller and higher, so it stands clear of the board; the place
  // (the tap target) is unchanged. The lift is a bottom padding: the centred drawing moves up by it.
  const renderDie = (slotPx: number, draw?: DieDraw): ReactNode => {
    const side = Math.max(44, Math.round(slotPx));
    // Without `draw`: the usual share (the scene's DIE_DRAW_SHARE / DIE_DRAW_MIN — keep in step).
    const dieSize = draw?.size ?? Math.max(28, Math.round(side * 0.72));
    const lift = draw?.lift ?? 0;
    return (
      <button
        type="button"
        data-testid="ladders-roll"
        aria-label={fa.ladders.roll}
        disabled={!canRoll}
        onClick={() => {
          if (canRoll) onMove({ kind: "roll" });
        }}
        className={`${BTN} flex flex-none items-center justify-center rounded-xl border-0 bg-transparent p-0 outline-none disabled:cursor-default`}
        style={{
          width: side,
          height: side,
          paddingBottom: lift > 0 ? 2 * lift : undefined,
          boxShadow: "none",
          outline: "none",
          opacity: view.die === null && !canRoll ? 0.55 : 1,
          touchAction: "manipulation",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        <Die3D
          key={track.rollCount}
          value={view.die ?? 6}
          size={dieSize}
          seed={`ladders-${track.rollCount}`}
          throwing={track.throwing}
          fromBottom={roller === you}
          launch={Math.round(dieSize * 2)}
          alwaysDrawn
          testId="ladders-die"
        />
      </button>
    );
  };

  const footer = (fit: LaddersSceneFit): ReactNode => (
    // `dir="rtl"`: the scene is `dir="ltr"` (the board is never mirrored), and the footer is
    // Persian — its own direction, so a line's punctuation and a name's order read right.
    // ⚠️ NO SAFE-AREA PADDING HERE: the scene owns both insets (W1's contract — the footer sits
    // inside its padded column), so a footer that padded the bottom inset again would lift itself
    // by the home indicator twice. Its own surface, a theme token.
    // ⚠️ COMPACT, AND FIXED DURING PLAY: the board needs the height, so the rows are tight; every
    // row is one line sized in `em` of its own text — the height never changes with what a row SAYS,
    // and it grows with a phone's text zoom instead of clipping. Round 2: no die and no roll button
    // here (the die is the control, above the board) — the strip, then the hint beside the timer.
    <div
      dir="rtl"
      data-testid="ladders-controls"
      className="mx-2 mb-1.5 flex flex-col gap-1 rounded-xl bg-surface-container/85 px-1.5 pb-1.5 pt-1 text-on-surface shadow-lg select-none"
    >
      {/* One strip per seated player, two to a row — the منچ strip. */}
      <div className="grid grid-cols-2 gap-x-1 gap-y-0.5">
        {Array.from({ length: seatCount }, (_, s) => strip(s, fit.width >= LADDERS_ROOMY_MIN_W))}
      </div>
      <div className="flex items-center gap-1">
        {/* The hint — ONE line, its height in `em` (fixed for a zoom, whatever it says). It may
            name a player (a hit): `dir="auto"` + every name a first-strong isolate, so a Latin name
            neither turns the line around nor loses its beginning to the ellipsis. */}
        <div
          data-testid="ladders-hint"
          dir="auto"
          className="min-w-0 flex-1 px-2 text-center font-bold text-secondary"
          style={{
            fontSize: 13,
            height: "1.5em",
            lineHeight: "1.5em",
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
          }}
        >
          {hint}
        </div>
        {/* The shared 32 px slot — offline (no timer at all) it is not drawn. */}
        <TurnTimerSlot timer={timer} />
      </div>
    </div>
  );

  return (
    <LaddersScene
      style={view.style}
      renderBoard={(sizePx) => (
        <LaddersBoard
          board={view.board}
          style={view.style}
          positions={view.positions}
          you={you}
          turn={playing ? view.turn : null}
          move={view.lastMove}
          moveKey={view.moveCount}
          roll={view.lastRoll}
          rollKey={view.rollCount}
          animate={track.animate}
          sizePx={sizePx}
          movable={movable}
          onPieceTap={() => {
            if (movable) onMove({ kind: "move" });
          }}
          onMotionEnd={onMotionEnd}
        />
      )}
      renderDie={renderDie}
      footer={footer}
    >
      {/* THE EXIT, where every table has it: the inline-start corner of the centred ≤ 440 px column,
          10 px under the top inset — `TableChat`'s fixed toggle sits 64 px in from that same corner
          on an online table. `dir="rtl"` because the scene is `ltr`. */}
      <div
        dir="rtl"
        className="pointer-events-none absolute inset-x-0 top-0 z-10"
        style={{
          paddingTop: "calc(10px + max(env(safe-area-inset-top), var(--tg-safe-top, 0px)))",
        }}
      >
        <div className="mx-auto flex w-full max-w-[440px] px-4">
          <button
            type="button"
            onClick={onLeave}
            aria-label={fa.ladders.leave}
            className={`${BTN} pointer-events-auto flex h-10 w-10 flex-none items-center justify-center rounded-full border border-outline-variant bg-surface-container/80 text-on-surface-variant shadow-md transition-transform hover:text-error active:scale-95`}
          >
            <GameIcon type="exit" size={20} />
          </button>
        </div>
      </div>
    </LaddersScene>
  );
}
